import React, { useEffect, useMemo, useState } from 'react'
import { Modal, ModalBody, ModalFooter, ModalHeader, ModalTitle } from 'react-bootstrap'
import DataTable from 'react-data-table-component'
import axios from 'axios'
import { getApiBaseUrl } from '../../utils/apiConfig'
import { getStorageValues } from '../../shared/CommonFunctions'
import { ReactComponent as SortArrow } from '../../shared/img/chevron-up-down.svg'

function PurchaseVehicleSelectionModal({
  show,
  onClose,
  onContinue,
  vehicleId,
  vehicleLabel,
  excludeKeys = []
}) {
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(false)
  const [selectedId, setSelectedId] = useState(null)
  const [error, setError] = useState('')

  const excludeSet = useMemo(() => new Set(excludeKeys), [excludeKeys])

  const fetchRows = async () => {
    if (!show) return
    try {
      setLoading(true)
      setError('')
      const response = await axios.get(`${getApiBaseUrl()}/purchaseregister/availabledetails`, {
        params: {
          UserId: getStorageValues('userId'),
          VehicleId: vehicleId || null
        }
      })
      const list = response?.data?.data || []
      const filtered = list.filter((row) => {
        const key = `${(row.EngineNo || '').toString().trim()}|${(row.ChassisNo || '').toString().trim()}`
        return key && !excludeSet.has(key)
      })
      setRows(filtered)
    } catch (err) {
      console.log(err)
      setError('Unable to load purchase vehicles.')
      setRows([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (show) {
      setSelectedId(null)
      fetchRows()
    }
  }, [show, vehicleId])

  const columns = [
    {
      name: '',
      width: '60px',
      center: true,
      cell: (row) => (
        <input
          type="radio"
          name="purchaseVehicle"
          checked={row.PurchaseRegisterDetailId === selectedId}
          onChange={() => setSelectedId(row.PurchaseRegisterDetailId)}
        />
      )
    },
    {
      name: <div className="titlename"><span>Vehicle</span></div>,
      selector: (row) => row.VehicleLabel,
      cell: (row) => <span title={row.VehicleLabel}>{row.VehicleLabel}</span>,
      sortable: true,
      sortIcon: <SortArrow />
    },
    {
      name: <div className="titlename"><span>Dealer</span></div>,
      selector: (row) => row.DealerName,
      cell: (row) => <span title={row.DealerName}>{row.DealerName}</span>,
      sortable: true,
      sortIcon: <SortArrow />
    },
    {
      name: <div className="titlename"><span>Purchase Date</span></div>,
      selector: (row) => row.PurchaseDate,
      cell: (row) => <span title={row.PurchaseDate}>{row.PurchaseDate}</span>,
      sortable: true,
      sortIcon: <SortArrow />
    },
    {
      name: <div className="titlename"><span>Chassis No</span></div>,
      selector: (row) => row.ChassisNo,
      cell: (row) => <span title={row.ChassisNo}>{row.ChassisNo}</span>,
      sortable: true,
      sortIcon: <SortArrow />
    },
    {
      name: <div className="titlename"><span>Engine No</span></div>,
      selector: (row) => row.EngineNo,
      cell: (row) => <span title={row.EngineNo}>{row.EngineNo}</span>,
      sortable: true,
      sortIcon: <SortArrow />
    }
  ]

  const selectedRow = rows.find((row) => row.PurchaseRegisterDetailId === selectedId) || null

  return (
    <Modal show={show} onHide={onClose} backdrop="static" size="lg">
      <ModalHeader closeButton>
        <ModalTitle>Purchase Vehicles {vehicleLabel ? `- ${vehicleLabel}` : ''}</ModalTitle>
      </ModalHeader>
      <ModalBody>
        {error && <div className="text-danger mb-2">{error}</div>}
        <DataTable
          columns={columns}
          data={rows}
          progressPending={loading}
          persistTableHead
        />
      </ModalBody>
      <ModalFooter>
        <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => onContinue?.(selectedRow)}
          disabled={!selectedRow}
        >
          Continue
        </button>
      </ModalFooter>
    </Modal>
  )
}

export default PurchaseVehicleSelectionModal
